/* Scentopia – Image overlay carousel
   - автоматична смяна с лента за оставащото време
   - стрелки и плъзгане с пръст / мишка
   - точки под изображението: толкова точки, колкото слайда има; активната е запълнена, с клик се отива на слайда
   - смяна с прелистване (data-transition="slide") или плавно избледняване (по подразбиране)
   - задържане (като Stories във Facebook/Instagram): докато пръстът (или мишката) е
     натиснат върху изображението, слайдът спира и лентата замръзва; при пускане продължава.
   - на компютър слайдът спира и докато мишката е върху карусела.
   - следващият слайд се зарежда предварително, за да не излиза празен при прелистване. */
(function () {
  if (customElements.get('overlay-carousel')) return;

  var HOLD_MS = 280; /* по-дълго натискане = задържане, а не клик */
  var SLIDE_MS = 700; /* малко повече от CSS анимацията (0.65s) */

  class OverlayCarousel extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.slides = Array.from(this.querySelectorAll('.ov-carousel__slide'));
      this.track = this.querySelector('.ov-carousel__track');
      this.progressBar = this.querySelector('.ov-carousel__progress-bar');
      this.prevButton = this.querySelector('[data-action="prev"]');
      this.nextButton = this.querySelector('[data-action="next"]');
      this.index = Math.max(0, this.slides.findIndex(function (s) { return s.classList.contains('is-active'); }));
      this.autoplayMs = parseInt(this.getAttribute('autoplay') || '0', 10);
      this.timer = null;
      this.animTimer = null;
      this.remaining = this.autoplayMs;
      this.startedAt = 0;
      this.pauseReasons = new Set();

      if (this.prevButton) this.prevButton.addEventListener('click', () => this.show(this.index - 1, -1));
      if (this.nextButton) this.nextButton.addEventListener('click', () => this.show(this.index + 1, 1));

      this.buildDots();
      this.setupPointer();

      /* компютър: спира, докато мишката е върху карусела */
      this.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.pause('hover'); });
      this.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') this.resume('hover'); });

      /* скрит раздел в браузъра – не върти слайдовете на празно */
      this._onVisibility = () => { document.hidden ? this.pause('hidden') : this.resume('hidden'); };
      document.addEventListener('visibilitychange', this._onVisibility);

      /* след като страницата се зареди – подготвяме следващия слайд */
      var preloadNext = () => this.preload(this.index + 1);
      if (document.readyState === 'complete') preloadNext();
      else window.addEventListener('load', preloadNext, { once: true });

      if (this.autoplayMs > 0 && this.slides.length > 1) this.startAutoplay();
    }

    disconnectedCallback() {
      this.clearTimer();
      if (this.animTimer) window.clearTimeout(this.animTimer);
      if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
    }

    get isSlideMode() {
      return this.getAttribute('data-transition') === 'slide' &&
        !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    }

    /* Точки под изображението – създават се автоматично според броя на слайдовете */
    buildDots() {
      this.dots = [];
      const old = this.querySelector(':scope > .ov-carousel__dots');
      if (old) old.remove();
      if (this.slides.length < 2) return;

      const nav = document.createElement('div');
      nav.className = 'ov-carousel__dots';
      nav.setAttribute('role', 'tablist');
      nav.setAttribute('aria-label', 'Слайдове');

      this.slides.forEach((slide, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ov-carousel__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Слайд ' + (i + 1) + ' от ' + this.slides.length);
        dot.addEventListener('click', () => {
          if (i === this.index) return;
          this.show(i, i > this.index ? 1 : -1);
        });
        nav.appendChild(dot);
        this.dots.push(dot);
      });

      this.appendChild(nav);
      this.updateDots();
    }

    updateDots() {
      if (!this.dots || !this.dots.length) return;
      this.dots.forEach((dot, i) => {
        const active = i === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    /* зарежда снимките на даден слайд предварително */
    preload(i) {
      const total = this.slides.length;
      if (total < 2) return;
      const slide = this.slides[((i % total) + total) % total];
      if (!slide) return;
      slide.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
    }

    /* Натискане, задържане и плъзгане */
    setupPointer() {
      const track = this.track;
      if (!track) return;

      let startX = 0, startY = 0, pointerId = null, dragging = false, downAt = 0, blockClick = false;

      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        pointerId = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
        dragging = false;
        blockClick = false;
        downAt = Date.now();
        this.pause('hold');
        this.classList.add('is-held');
      });

      track.addEventListener('pointermove', (e) => {
        if (e.pointerId !== pointerId) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        if (!dragging && this.slides.length > 1 && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
          dragging = true;
          track.classList.add('is-dragging');
          try { track.setPointerCapture(pointerId); } catch (err) {}
        }
        /* вертикален скрол на страницата – това не е задържане */
        if (!dragging && Math.abs(dy) > 12) {
          this.classList.remove('is-held');
          this.resume('hold');
        }
      });

      const release = (e, cancelled) => {
        if (e.pointerId !== pointerId) return;
        pointerId = null;
        track.classList.remove('is-dragging');
        this.classList.remove('is-held');

        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        if (!cancelled && dragging && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
          blockClick = true;
          this.pauseReasons.delete('hold');
          const dir = dx < 0 ? 1 : -1;
          this.show(this.index + dir, dir);
        } else {
          /* дълго задържане – не отваряме линка при пускане */
          if (Date.now() - downAt > HOLD_MS) blockClick = true;
          this.resume('hold');
        }
        dragging = false;
      };

      track.addEventListener('pointerup', (e) => release(e, false));
      track.addEventListener('pointercancel', (e) => release(e, true));

      track.addEventListener('click', (e) => {
        if (blockClick) {
          e.preventDefault();
          e.stopPropagation();
          blockClick = false;
        }
      }, true);

      /* без системното меню „Запази изображението“ при дълго натискане на телефон */
      track.addEventListener('contextmenu', (e) => {
        if (Date.now() - downAt > HOLD_MS) e.preventDefault();
      });
    }

    /* dir: 1 = напред (новият идва отдясно), -1 = назад (идва отляво) */
    show(nextIndex, dir) {
      const total = this.slides.length;
      if (!total) return;

      const prevIndex = this.index;
      const newIndex = ((nextIndex % total) + total) % total;
      if (dir !== 1 && dir !== -1) dir = nextIndex < prevIndex ? -1 : 1;

      if (newIndex !== prevIndex && this.isSlideMode) {
        this.animateSlide(this.slides[prevIndex], this.slides[newIndex], dir);
      }

      this.index = newIndex;
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
      this.updateDots();
      this.preload(this.index + 1);

      if (this.autoplayMs > 0 && total > 1) this.startAutoplay();
    }

    /* Прелистване: старият слайд излиза настрани, новият влиза от другата страна */
    animateSlide(oldSlide, newSlide, dir) {
      /* приключваме предишна недовършена анимация */
      if (this.animTimer) {
        window.clearTimeout(this.animTimer);
        this.animTimer = null;
      }
      this.slides.forEach(function (s) {
        s.style.transition = '';
        s.style.transform = '';
        s.style.visibility = '';
        s.style.zIndex = '';
      });

      /* новият застава извън кадъра (без анимация) */
      newSlide.style.transition = 'none';
      newSlide.style.transform = 'translate3d(' + (dir * 100) + '%, 0, 0)';
      newSlide.style.visibility = 'visible';
      oldSlide.style.visibility = 'visible';
      oldSlide.style.zIndex = '2';
      void newSlide.offsetWidth; /* прилагаме началната позиция */

      newSlide.style.transition = '';
      newSlide.style.transform = 'translate3d(0, 0, 0)';
      oldSlide.style.transform = 'translate3d(' + (-dir * 100) + '%, 0, 0)';

      this.animTimer = window.setTimeout(() => {
        this.animTimer = null;
        oldSlide.style.transition = 'none';
        oldSlide.style.transform = '';
        oldSlide.style.visibility = '';
        oldSlide.style.zIndex = '';
        newSlide.style.transform = '';
        newSlide.style.visibility = '';
        void oldSlide.offsetWidth;
        oldSlide.style.transition = '';
      }, SLIDE_MS);
    }

    startAutoplay() {
      this.clearTimer();
      this.remaining = this.autoplayMs;
      this.setProgress(0, 0);
      if (this.progressBar) void this.progressBar.offsetWidth; /* нулиране преди новата анимация */
      if (this.pauseReasons.size) return; /* спрян – тръгва при resume() */
      this.run();
    }

    run() {
      if (this.pauseReasons.size || this.autoplayMs <= 0) return;
      this.clearTimer();
      this.startedAt = Date.now();
      if (this.progressBar) void this.progressBar.offsetWidth;
      this.setProgress(100, this.remaining);
      this.timer = window.setTimeout(() => this.show(this.index + 1, 1), this.remaining);
    }

    pause(reason) {
      if (this.autoplayMs <= 0 || this.slides.length < 2) return;
      const wasRunning = this.pauseReasons.size === 0;
      this.pauseReasons.add(reason);
      if (!wasRunning) return;

      if (this.timer) {
        this.clearTimer();
        this.remaining = Math.max(0, this.remaining - (Date.now() - this.startedAt));
      }
      /* лентата замръзва точно там, където е стигнала */
      if (this.progressBar) {
        const full = this.progressBar.parentElement.getBoundingClientRect().width || 1;
        const now = this.progressBar.getBoundingClientRect().width;
        this.setProgress((now / full) * 100, 0);
      }
      this.classList.add('is-paused');
    }

    resume(reason) {
      if (!this.pauseReasons.has(reason)) return;
      this.pauseReasons.delete(reason);
      if (this.pauseReasons.size) return;
      this.classList.remove('is-paused');
      this.run();
    }

    clearTimer() {
      if (this.timer) {
        window.clearTimeout(this.timer);
        this.timer = null;
      }
    }

    setProgress(percent, durationMs) {
      if (!this.progressBar) return;
      this.progressBar.style.transitionDuration = durationMs + 'ms';
      this.progressBar.style.width = percent + '%';
    }
  }

  customElements.define('overlay-carousel', OverlayCarousel);
})();
